import React, { PropsWithChildren, useState, useMemo, useCallback, memo, useEffect, useRef } from "react";
import useCacheManager from "./useCacheManager";
import RefreshConfirmationModal from "./RefreshConfirmationModal";
import { useWatchAuthenticationState } from "../../../hooks/useWatchAuthenticationState";
import { useMatch, useNavigate } from "react-router-dom"
import useStoreAccess from "../../../hooks/useStoreAccess";
import { useGetCurrentUserQuery } from "../../../queries/useGetCurrentUserQuery";
import Background from "../../layout/Background/Background.";
import Api from "../../../api/Api";

type Props = PropsWithChildren<{}>;

type ModalState = {
  showConfirmation: boolean;
};

function CacheSessionModal({ children }: Props) {
  useStoreAccess();

  const wasAuthenticated = useWatchAuthenticationState();
  const isHardRedirecting = useRef(false);

  const {hasCache, clearCache} = useCacheManager()
  const {refetch} = useGetCurrentUserQuery()

  const navigate = useNavigate();

  const [modalState, setModalState] = useState<ModalState>({
    showConfirmation: true,
  });

  const isPaymentSuccessRoute = useMatch({
    path: '/paymentsuccess',
    end: true
  })

  useEffect(() => {
    if (isHardRedirecting.current) return
    if (hasCache === false && consumeWasSignedInBefore()) return
    if (hasCache === null) return

    if (consumePostLoginFlag()) {
      handleContinueSession()
    }
  }, [hasCache])


  useEffect(() => {
    if (isHardRedirecting.current) return
    if (isPaymentSuccessRoute && hasCache) {
      handleRefresh()
    }
  }, [isPaymentSuccessRoute, hasCache])


  const { shouldShowSpinner } = useMemo(
    () => ({
      shouldShowSpinner: modalState.showConfirmation, 
    }),
    [modalState.showConfirmation]
  );

  const consumePostLoginFlag = useCallback(() => {
    const key = "skipCacheModalOnce"
    const has = sessionStorage.getItem(key) === "1"
    if (has) sessionStorage.removeItem(key)
    return has
  }, []);


  const consumeWasSignedInBefore = useCallback(() => {
    const key = "wasSignedInBefore"
    const val = sessionStorage.getItem(key) === "1"
    if (val) sessionStorage.removeItem(key)
    return val
  }, []);


  const handleContinueSession = useCallback(async () => {
    setModalState({ showConfirmation: false });
    const wasSignedInBefore = consumeWasSignedInBefore()
    if (wasSignedInBefore && !wasAuthenticated) {
      isHardRedirecting.current = true
      window.location.replace(`${Api.SERVER_DOMAIN}${Api.IISSite}/auth/signin`)
      return
    } else {
      await refetch().catch(console.error)
      navigate('/')
    }

  }, [navigate, refetch, wasAuthenticated])

  const handleCancel = useCallback(() => {
    handleContinueSession();
  }, [handleContinueSession]);

  const handleRefresh = useCallback(async () => {
    try {
      consumeWasSignedInBefore()
      await clearCache();
      setModalState({ showConfirmation: false })
      navigate(isPaymentSuccessRoute ? '/payment/success' : '/');
    } catch (error) {
      console.error("Error clearing cache:", error)
    }
  }, [clearCache, navigate, isPaymentSuccessRoute]);

  if (hasCache === null) {
    return <></>
  }

  if (isHardRedirecting.current) {
    return <></>
  }

  if (!hasCache) return <>{children}</>;

  if (shouldShowSpinner) {
    return (
      <>
        <RefreshConfirmationModal onCancel={handleCancel} onRefresh={handleRefresh} />
        <Background />
      </>
    );
  }
  
  return <>{children}</>;
}

export default memo(CacheSessionModal);
